import React, { useCallback, useRef, useState } from 'react';

import i18n from '@nextroll/ar-i18n';

import { ExpandablePanel } from './ExpandablePanel';
import { Chips } from '../Chips/Chips';


export const ExpandablePanelJobTitleFilter = ({
    includedFilters, setIncludedFilters,
    excludedFilters, setExcludedFilters,
}: {
    includedFilters: string[];
    setIncludedFilters: (filters: string[]) => void;
    excludedFilters: string[];
    setExcludedFilters: (filters: string[]) => void;
}) => {
    const [keyword, setKeyword] = useState('');
    const [excluded, setExcluded] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    /**
     * when a new keyword is added from the input box
     */
    const handleAdd = useCallback((e: React.KeyboardEvent) => {
        if (e.key !== 'Enter') {
            return;
        }
        e.stopPropagation();

        // job title search is not case sensitive; need to convert them to lowercase before comparisons
        const keywordLowerCase = keyword.toLowerCase();
        if (!keyword || [...includedFilters, ...excludedFilters].some(f => f.toLowerCase() === keywordLowerCase)) {
            inputRef.current?.classList.add('shaking');
            setTimeout(() => {
                inputRef.current?.classList.remove('shaking');
            }, 250);
            return; // empty or duplicated keyword; do nothing
        }

        // valid input; update the corresponding filter list
        const updatedFilters = [...(excluded ? excludedFilters : includedFilters), keyword];
        (excluded ? setExcludedFilters : setIncludedFilters)(updatedFilters);

        // also don't forget to cleanup the text box
        setKeyword('');
    }, [excluded, includedFilters, setIncludedFilters, excludedFilters, setExcludedFilters, keyword, setKeyword]);

    /**
     * when an existing keyword is deleted from the chips
     */
    const handleDelete = useCallback((_: any, keywordToDelete: string) => {
        setIncludedFilters([...includedFilters].filter(item => item !== keywordToDelete));
        setExcludedFilters([...excludedFilters].filter(item => item !== keywordToDelete));
    }, [includedFilters, setIncludedFilters, excludedFilters, setExcludedFilters]);

    /**
     * toggle between inclusion and exclusion using the button group
     */
    const handleToggle = useCallback((updatedExclusion: boolean) => () => {
        if (updatedExclusion === excluded) {
            return;
        }
        setExcluded(!excluded);
        inputRef.current?.focus(); // pass focus to the input box instantly
    }, [excluded, setExcluded]);

    return (
        <ExpandablePanel
            title={i18n.gettext('Job Title')}
            icon={<i className='fas fa-user' />}
            items={[...includedFilters, ...excludedFilters]}
            setItems={items => {
                setExcludedFilters(items);
                setIncludedFilters(items);
            }}
            className={{ body: 'workflow-node-panel-filter-job-title' }}
        >
            <Chips
                labels={[...includedFilters, ...excludedFilters]}
                validate={label => label.length > 2}
                include={label => includedFilters.includes(label)}
                exclude={label => excludedFilters.includes(label)}
                error={i18n.gettext('Each keyword must be a minimum of 3 characters')}
                onDelete={handleDelete}
            />
            <input
                type='text'
                ref={inputRef}
                placeholder={i18n.gettext('Type keyword(s) and press Enter (e.g., Marketing, Marketer)')}
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={handleAdd}
                className='form-control'
            />
            <div className='btn-group' role='group'>
                <button type='button' role='button' className={`btn btn-sm btn-default${excluded ? '' : ' active'}`} onClick={handleToggle(false)}>
                    {i18n.gettext('Include')}
                </button>
                <button type='button' role='button' className={`btn btn-sm btn-default${excluded ? ' active' : ''}`} onClick={handleToggle(true)}>
                    {i18n.gettext('Exclude')}
                </button>
            </div>
            <i role='button' className='fas fa-times' onClick={() => keyword && setKeyword('')} />
        </ExpandablePanel>
    );
};
